import {
	EXPLOSION_SOUND_RADIUS,
	findEntity,
	type PlayerIndex,
	type SimEvent,
	type WorldState,
} from "../../sim";
import { wrappedAxisDelta, wrappedDistance } from "../../sim/math/torus";
import type { Vec2 } from "../../sim/math/vec2";
import type { SoundKey } from "../assets";

export interface SoundCue {
	key: SoundKey;
	/** Stereo position, -1 (left) … 1 (right). */
	pan: number;
}

/**
 * A human viewer the sound mix is centred on. `bias` is the viewport's side of the screen:
 * -0.5 for the left half of a split screen, 0.5 for the right half, 0 when full screen.
 */
export interface Listener {
	player: PlayerIndex;
	bias: number;
}

/** Horizontal distance (world units) at which a sound is panned fully to one side of its viewer. */
const PAN_DISTANCE = 800;

/**
 * legacy: SoundManager.cpp. Laser/missile on fire, "boing" on ship bounces (and shield hits), a
 * pickup chime for collected power-up pods (new, P10.10), explosions for
 * every ship and for other destructions within 500 units of any player's ship.
 * Distance uses the shortest wrapped distance (legacy ignored the seam). New: each cue is
 * panned towards where it happens (see `panFor`).
 */
export function soundsForEvents(
	events: readonly SimEvent[],
	world: WorldState,
	listeners: readonly Listener[] = [],
): SoundCue[] {
	const cues: SoundCue[] = [];
	const cue = (key: SoundKey, pos: Vec2) => cues.push({ key, pan: panFor(pos, world, listeners) });
	for (const ev of events) {
		switch (ev.type) {
			case "fired":
				// A triple-laser volley sounds like one shot.
				if (!ev.side) cue(ev.weapon === "laser" ? "laser" : "missile", ev.pos);
				break;
			case "boing":
			case "shieldHit":
				cue("boing", ev.pos);
				break;
			case "pickedUp":
				cue("pickup", ev.pos);
				break;
			// Objective modes (P11.2): flags taken and captures chime; hill seconds stay quiet.
			case "flagTaken":
			case "flagReturned": {
				const flag = world.objective?.kind === "ctf" ? world.objective.flags[ev.owner] : undefined;
				if (flag) cue("pickup", flag.pos);
				break;
			}
			case "bonusShip":
				cue("pickup", { x: 0, y: 0 });
				break;
			case "scored":
				if (ev.how === "capture") cue("boing", { x: 0, y: 0 });
				break;
			case "destroyed":
				if (ev.kind === "ship" || nearAnyShip(world, ev.cause.pos)) cue("explosion", ev.pos);
				break;
			default:
				break;
		}
	}
	return cues;
}

/**
 * Pans a sound towards the viewer whose ship is nearest to it: that viewport's side of the
 * screen, plus the horizontal offset from their ship. Without listeners the sound is centred.
 */
export function panFor(pos: Vec2, world: WorldState, listeners: readonly Listener[]): number {
	let best: { listener: Listener; dx: number; dist: number } | null = null;
	for (const listener of listeners) {
		const shipId = world.players[listener.player]?.shipId;
		const ship = shipId == null ? undefined : findEntity(world, shipId);
		if (!ship) continue;
		const dist = wrappedDistance(ship.pos, pos, world.size);
		if (!best || dist < best.dist) {
			best = { listener, dx: wrappedAxisDelta(ship.pos.x, pos.x, world.size.width), dist };
		}
	}
	if (!best) return listeners.length === 1 ? (listeners[0]?.bias ?? 0) : 0;
	const pan = best.listener.bias + (best.dx / PAN_DISTANCE) * 0.5;
	return Math.max(-1, Math.min(1, pan));
}

function nearAnyShip(world: WorldState, pos: Vec2): boolean {
	return world.players.some((p) => {
		const ship = p.shipId === null ? undefined : findEntity(world, p.shipId);
		return (
			ship !== undefined && wrappedDistance(ship.pos, pos, world.size) < EXPLOSION_SOUND_RADIUS
		);
	});
}
